import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Badge, Button, Card, ConfirmDialog, EmptyState, LoadingList, SectionHeader,
    cx, useIsPhone,
} from '@timely/ui';
import {
    CHEQUE_STATES, CHEQUE_STATE_ORDER, ChequeStatus, chequeActions, holds,
    isChequeState, type ChequeState,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import { ChequeDialog } from '../cheques/ChequeDialog';
import {
    CHEQUE_PAGE, DEFAULT_CHEQUE_FILTERS, changeChequeStatus, deleteCheque, stateOf,
    useChequeRegister, type ChequeFilters, type ChequeRow, type ChequeTotals,
} from '../cheques/useCheques';

/**
 * The cheque register.
 *
 * One list of every cheque, six compact tiles above it that are both the
 * overview and the filter, a filter bar, and the actions for a row on the row.
 * That shape is the reference product's, arrived at after a three-tab redesign
 * that scattered the same cheques across "needs attention", "coming up" and
 * "finished" and left people hunting for one they had just seen.
 *
 * Two rules it keeps:
 *
 *   **A tile counts exactly what pressing it opens.** Both come from
 *   `cheque_totals()`, which counts each state with every *other* filter
 *   applied — so "Bounced 3" next to a bank filter means three at that bank,
 *   and pressing it shows those three.
 *
 *   **A row carries only the actions its state has.** A cleared cheque has one
 *   quiet Undo and no Clear; offering Clear on it is how somebody clears it
 *   twice and wonders why the total moved. The list comes from
 *   `chequeActions()`, and the database refuses the transition as well —
 *   a hidden button is not an enforcement.
 */
export function Cheques() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { money, count, date, today } = useFormatters();
    const isPhone = useIsPhone();

    const canManage = holds(context, 'cheques.manage');

    const [params, setParams] = useSearchParams();
    const filters = filtersFromParams(params);

    const { rows, totals, banks, loading, loadingMore, error, hasMore, reload, loadMore } =
        useChequeRegister(workspace.id, filters);

    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [editing, setEditing] = useState<ChequeRow | 'new' | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<ChequeRow | null>(null);

    const set = (changes: Partial<ChequeFilters>) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            const isDefault = value === '' || value === 'any' || value === undefined;
            if (isDefault) next.delete(key); else next.set(key, String(value));
        }
        setParams(next, { replace: true });
    };

    const narrowed =
        (filters.state !== 'any' ? 1 : 0) + (filters.search ? 1 : 0)
        + (filters.owner !== 'any' ? 1 : 0) + (filters.bank !== 'any' ? 1 : 0)
        + (filters.from ? 1 : 0) + (filters.to ? 1 : 0);

    const act = async (row: ChequeRow, status: ChequeStatus, label: string) => {
        if (busyId) return;
        setBusyId(row.id);
        setNotice(null);
        try {
            await changeChequeStatus(workspace.id, row.id, status, row.updatedAt);
            await reload();
            setNotice({ tone: 'ok', text: `${row.chequeNumber || 'That cheque'} — ${label.toLowerCase()}.` });
        } catch (failed) {
            setNotice({ tone: 'error', text: failed instanceof Error ? failed.message : 'That did not work.' });
            // A conflict means this screen is out of date. Reading it again is
            // the only honest thing to show next.
            await reload();
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Page>
            <SectionHeader
                title="Cheques"
                subtitle="Every cheque you hold, and what needs doing with it."
                actions={canManage ? (
                    <Button variant="primary" onClick={() => setEditing('new')}>Record a cheque</Button>
                ) : undefined}
            />

            {notice && <div className="mt-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}

            <StateTiles
                totals={totals}
                active={filters.state}
                onPick={state => set({ state: state === filters.state ? 'any' : state })}
            />

            <Filters
                filters={filters}
                banks={banks}
                narrowed={narrowed}
                onChange={set}
                onClear={() => setParams(new URLSearchParams(), { replace: true })}
            />

            {totals && (
                <div className="mt-4 flex items-baseline gap-2 flex-wrap text-[12.5px] text-label-3">
                    <strong className="text-[15px] text-label num tabular-nums">
                        {money.exact(totals.matchedAmount)}
                    </strong>
                    <span>
                        across {count(totals.matchedCount)}{' '}
                        {totals.matchedCount === 1 ? 'cheque' : 'cheques'}
                        {totals.matchedCount !== totals.visibleCount && (
                            <> of {count(totals.visibleCount)}</>
                        )}
                    </span>
                    {rows.length < totals.matchedCount && (
                        <span className="text-label-3">· showing {count(rows.length)}</span>
                    )}
                </div>
            )}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading your cheques" rows={6} />
                ) : error ? (
                    <div className="px-5 py-6">
                        <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                    </div>
                ) : totals !== null && totals.visibleCount === 0 ? (
                    <EmptyState
                        title="No cheques yet"
                        hint={canManage
                            ? 'Record the first cheque somebody has given you, and it appears here with its date and what needs doing.'
                            : 'Once somebody records a cheque, it appears here.'}
                        action={canManage
                            ? <Button variant="primary" onClick={() => setEditing('new')}>Record your first cheque</Button>
                            : undefined}
                    />
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="Nothing matches"
                        hint={totals
                            ? `None of your ${count(totals.visibleCount)} cheques match what you are looking for.`
                            : 'No cheque matches what you are looking for.'}
                        action={
                            <Button
                                variant="secondary"
                                onClick={() => setParams(new URLSearchParams(), { replace: true })}
                            >
                                Clear filters
                            </Button>
                        }
                    />
                ) : isPhone ? (
                    <ul className="divide-y divide-separator">
                        {rows.map(row => (
                            <ChequeCard
                                key={row.id} row={row} today={today} slug={workspace.slug}
                                canManage={canManage} busy={busyId === row.id}
                                onAct={act} onEdit={() => setEditing(row)}
                                onDelete={() => setConfirmDelete(row)}
                            />
                        ))}
                    </ul>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]">
                            <thead>
                                <tr className="text-left text-label-3 text-[12px]">
                                    <th scope="col" className="font-semibold px-5 py-2.5">Customer</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5">Cheque</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5">Bank</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5">Dated</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5 text-right">Amount</th>
                                    <th scope="col" className="font-semibold px-3 py-2.5">State</th>
                                    <th scope="col" className="font-semibold px-5 py-2.5 text-right">
                                        {canManage ? 'What next' : ''}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-separator">
                                {rows.map(row => (
                                    <ChequeTableRow
                                        key={row.id} row={row} today={today} slug={workspace.slug}
                                        canManage={canManage} busy={busyId === row.id}
                                        onAct={act} onEdit={() => setEditing(row)}
                                        onDelete={() => setConfirmDelete(row)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {hasMore && !loading && (
                <div className="mt-5 text-center">
                    <Button variant="secondary" disabled={loadingMore} onClick={() => { void loadMore(); }}>
                        {loadingMore ? 'Loading…' : totals
                            ? `Load more (${count(rows.length)} of ${count(totals.matchedCount)})`
                            : `Load more (${count(rows.length)} so far)`}
                    </Button>
                </div>
            )}

            {editing && (
                <ChequeDialog
                    cheque={editing === 'new' ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async message => {
                        setEditing(null);
                        await reload();
                        setNotice({ tone: 'ok', text: message });
                    }}
                />
            )}

            <ConfirmDialog
                open={confirmDelete !== null}
                title="Delete this cheque?"
                confirmLabel="Delete it"
                onCancel={() => setConfirmDelete(null)}
                onConfirm={() => {
                    const row = confirmDelete;
                    setConfirmDelete(null);
                    if (!row) return;
                    void (async () => {
                        try {
                            await deleteCheque(workspace.id, row.id);
                            await reload();
                            setNotice({ tone: 'ok', text: 'That cheque has been deleted.' });
                        } catch (failed) {
                            setNotice({
                                tone: 'error',
                                text: failed instanceof Error ? failed.message : 'That did not work.',
                            });
                        }
                    })();
                }}
            >
                <p>
                    {confirmDelete?.chequeNumber
                        ? `Cheque ${confirmDelete.chequeNumber}`
                        : 'This cheque'}
                    {confirmDelete ? ` for ${money.exact(confirmDelete.amount)}` : ''}
                    {confirmDelete?.customerName ? `, from ${confirmDelete.customerName}` : ''}.
                    {' '}Deleting it removes it from the register and from every total.
                    If it bounced or was cancelled, say so instead — that keeps the history.
                </p>
            </ConfirmDialog>

            {/* The register's own dates are the company's, not the browser's. */}
            <p className="sr-only">Dates are shown in {date.zoneLabel}.</p>
        </Page>
    );
}

/* -------------------------------- the tiles ------------------------------- */

const TILE_TONE = {
    dang: 'text-dang', warn: 'text-warn', brand: 'text-accent',
    pos: 'text-pos', neutral: 'text-label',
} as const;

/**
 * Six tiles: the overview and the filter in one control.
 *
 * Each is a real button with `aria-pressed`, so a keyboard reaches them in
 * order and a screen reader says which one is on. Pressing the active tile
 * again clears it, which is what people try first.
 */
function StateTiles({
    totals, active, onPick,
}: {
    totals: ChequeTotals | null;
    active: ChequeState | 'any';
    onPick: (state: ChequeState) => void;
}) {
    const { money, count } = useFormatters();

    return (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {CHEQUE_STATE_ORDER.map(state => {
                const figure = totals?.byState[state] ?? { count: 0, amount: 0 };
                const on = active === state;
                return (
                    <button
                        key={state}
                        type="button"
                        aria-pressed={on}
                        title={CHEQUE_STATES[state].hint}
                        onClick={() => onPick(state)}
                        className={cx(
                            'text-left rounded-[12px] border px-3 py-2.5 min-h-[64px] transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                            on
                                ? 'border-accent bg-accent-soft'
                                : 'border-separator bg-card hover:border-separator-strong',
                        )}
                    >
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-[12px] font-semibold text-label-2 truncate">
                                {CHEQUE_STATES[state].label}
                            </span>
                            <span className={cx(
                                'text-[17px] font-bold num tabular-nums leading-none',
                                figure.count === 0 ? 'text-label-3' : TILE_TONE[CHEQUE_STATES[state].tone],
                            )}>
                                {count(figure.count)}
                            </span>
                        </div>
                        <div className="text-[11.5px] text-label-3 num mt-1">
                            {figure.count === 0 ? '—' : money.compact(figure.amount)}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

/* ------------------------------- the filters ------------------------------ */

function Filters({
    filters, banks, narrowed, onChange, onClear,
}: {
    filters: ChequeFilters;
    banks: { bankName: string; chequeCount: number }[];
    narrowed: number;
    onChange: (changes: Partial<ChequeFilters>) => void;
    onClear: () => void;
}) {
    const [open, setOpen] = useState(false);

    return (
        <div className="mt-4">
            <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <label htmlFor="cheque-search" className="block text-[12.5px] font-semibold text-label mb-1">
                        Search
                    </label>
                    <input
                        id="cheque-search"
                        type="search"
                        value={filters.search}
                        placeholder="Customer, cheque number, bank"
                        onChange={e => onChange({ search: e.target.value })}
                        className="w-full h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
                    />
                </div>

                <Button
                    variant="secondary"
                    aria-expanded={open}
                    aria-controls="cheque-more-filters"
                    onClick={() => setOpen(v => !v)}
                    className="mb-0"
                >
                    {open ? 'Fewer filters' : 'More filters'}
                    {narrowed > 0 && <span className="ml-1.5 text-accent font-bold">{narrowed}</span>}
                </Button>

                {narrowed > 0 && (
                    <Button variant="quiet" onClick={onClear}>Show all</Button>
                )}
            </div>

            <div id="cheque-more-filters" hidden={!open} className="mt-3 pt-3 border-t border-separator">
                <div className="flex items-end gap-3 flex-wrap">
                    <div>
                        <label htmlFor="cheque-bank" className="block text-[12.5px] font-semibold text-label mb-1">
                            Bank
                        </label>
                        <select
                            id="cheque-bank"
                            value={filters.bank}
                            onChange={e => onChange({ bank: e.target.value })}
                            className="h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent min-w-[160px]"
                        >
                            <option value="any">Any bank</option>
                            {banks.map(b => (
                                <option key={b.bankName} value={b.bankName}>
                                    {b.bankName} ({b.chequeCount})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="cheque-from" className="block text-[12.5px] font-semibold text-label mb-1">
                            Dated from
                        </label>
                        <input
                            id="cheque-from" type="date" value={filters.from}
                            onChange={e => onChange({ from: e.target.value })}
                            className="h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
                        />
                    </div>
                    <div>
                        <label htmlFor="cheque-to" className="block text-[12.5px] font-semibold text-label mb-1">
                            Dated to
                        </label>
                        <input
                            id="cheque-to" type="date" value={filters.to}
                            onChange={e => onChange({ to: e.target.value })}
                            className="h-10 px-3 rounded-[10px] bg-card border border-separator-strong text-[13.5px] text-label outline-none focus:border-accent"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

/* -------------------------------- the rows -------------------------------- */

const BADGE_TONE = {
    dang: 'dang', warn: 'warn', brand: 'brand', pos: 'pos', neutral: 'neutral',
} as const;

interface RowProps {
    row: ChequeRow;
    today: Date;
    slug: string;
    canManage: boolean;
    busy: boolean;
    onAct: (row: ChequeRow, status: ChequeStatus, label: string) => void | Promise<void>;
    onEdit: () => void;
    onDelete: () => void;
}

function ChequeTableRow({ row, today, slug, canManage, busy, onAct, onEdit, onDelete }: RowProps) {
    const { money, date } = useFormatters();
    const state = stateOf(row, today);

    return (
        <tr className={cx(busy && 'opacity-60')}>
            <td className="px-5 py-2.5">
                {row.customerId ? (
                    <Link
                        to={`/w/${slug}/book/${row.customerId}`}
                        className="text-label font-semibold hover:text-accent hover:underline"
                    >
                        {row.customerName || 'That account'}
                    </Link>
                ) : (
                    <span className="text-label-2">{row.customerName || 'Not linked'}</span>
                )}
            </td>
            <td className="px-3 py-2.5 text-label-2 num">{row.chequeNumber || '—'}</td>
            <td className="px-3 py-2.5 text-label-2">{row.bankName || '—'}</td>
            <td className="px-3 py-2.5 text-label-2 num whitespace-nowrap">
                {row.chequeDate ? date.day(row.chequeDate) : '—'}
            </td>
            <td className="px-3 py-2.5 text-right num tabular-nums text-label font-semibold">
                {money.exact(row.amount)}
            </td>
            <td className="px-3 py-2.5">
                <Badge tone={BADGE_TONE[CHEQUE_STATES[state].tone]} title={CHEQUE_STATES[state].hint}>
                    {CHEQUE_STATES[state].label}
                </Badge>
            </td>
            <td className="px-5 py-2.5">
                {canManage && (
                    <div className="flex items-center gap-1.5 justify-end flex-wrap">
                        {chequeActions(state).map(action => (
                            <Button
                                key={action.label}
                                size="sm"
                                variant={action.tone === 'pos' ? 'primary' : 'quiet'}
                                title={action.title}
                                disabled={busy}
                                onClick={() => { void onAct(row, action.to, action.label); }}
                            >
                                {action.label}
                            </Button>
                        ))}
                        <Button size="sm" variant="ghost" onClick={onEdit}
                            title="Correct this cheque's details">Edit</Button>
                        <Button size="sm" variant="ghost" onClick={onDelete}
                            title="Remove this cheque from the register">Delete</Button>
                    </div>
                )}
            </td>
        </tr>
    );
}

/**
 * One cheque on a phone.
 *
 * A card rather than a squeezed row: six columns at 360px is six unreadable
 * columns. Every action stays visible and at least 40px tall — nothing hides
 * behind hover, which does not exist here, or behind an overflow menu, which
 * makes the ordinary job take two presses.
 */
function ChequeCard({ row, today, slug, canManage, busy, onAct, onEdit, onDelete }: RowProps) {
    const { money, date } = useFormatters();
    const state = stateOf(row, today);

    return (
        <li className={cx('px-4 py-3.5', busy && 'opacity-60')}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    {row.customerId ? (
                        <Link
                            to={`/w/${slug}/book/${row.customerId}`}
                            className="text-[14.5px] font-semibold text-label hover:text-accent hover:underline block truncate"
                        >
                            {row.customerName || 'That account'}
                        </Link>
                    ) : (
                        <span className="text-[14.5px] font-semibold text-label-2 block truncate">
                            {row.customerName || 'Not linked'}
                        </span>
                    )}
                    <div className="text-[12.5px] text-label-3 mt-0.5 num">
                        {row.chequeNumber || 'No number'}
                        {row.bankName ? ` · ${row.bankName}` : ''}
                    </div>
                </div>
                <div className="text-right flex-none">
                    <div className="text-[15px] font-bold text-label num tabular-nums">
                        {money.exact(row.amount)}
                    </div>
                    <div className="text-[12px] text-label-3 num">
                        {row.chequeDate ? date.day(row.chequeDate) : 'Undated'}
                    </div>
                </div>
            </div>

            <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Badge tone={BADGE_TONE[CHEQUE_STATES[state].tone]}>{CHEQUE_STATES[state].label}</Badge>
                <span className="text-[12px] text-label-3">{CHEQUE_STATES[state].hint}</span>
            </div>

            {canManage && (
                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                    {chequeActions(state).map(action => (
                        <Button
                            key={action.label}
                            size="sm"
                            variant={action.tone === 'pos' ? 'primary' : 'secondary'}
                            title={action.title}
                            disabled={busy}
                            className="min-h-[40px]"
                            onClick={() => { void onAct(row, action.to, action.label); }}
                        >
                            {action.label}
                        </Button>
                    ))}
                    <Button size="sm" variant="quiet" className="min-h-[40px]" onClick={onEdit}>Edit</Button>
                    <Button size="sm" variant="quiet" className="min-h-[40px]" onClick={onDelete}>Delete</Button>
                </div>
            )}
        </li>
    );
}

/* -------------------------------------------------------------------------- */

/**
 * The filters, read out of the address bar.
 *
 * So a filtered register can be linked to — Today's "3 bounced" opens exactly
 * that — and a back press from an account returns to the list as it was.
 * Anything unrecognised falls back to the default rather than being trusted.
 */
export function filtersFromParams(params: URLSearchParams): ChequeFilters {
    const state = params.get('state');
    const day = (value: string | null): string =>
        value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';

    return {
        state: isChequeState(state) ? state : 'any',
        search: params.get('search') ?? '',
        owner: params.get('owner') ?? DEFAULT_CHEQUE_FILTERS.owner,
        bank: params.get('bank') ?? DEFAULT_CHEQUE_FILTERS.bank,
        from: day(params.get('from')),
        to: day(params.get('to')),
    };
}

export { CHEQUE_PAGE };
